import katalReady from '@amzn/katal-components-ready';
import { domReady } from '../utils/dom-ready';
import { createLogger, filterError } from './logger';
import { createMetricsPublisher } from './metrics-publisher';
import { collectComponentMetrics } from './component-metrics';

export function logMetrics() {
  const { publisher, sushiDriver } = createMetricsPublisher();
  const logger = createLogger();

  collectComponentMetrics(publisher, sushiDriver);

  // Need a global function the asset loader can use to send metrics about the component asset.
  // In the event that components fail to load KatalReady won't fire so we need this function to be used by the asset loader.
  window.Katal.internalMetricsAssetLogger = function (counterMetric, value) {
    publisher.publishCounterMonitor(counterMetric, value);
  };

  window.Katal.internalAssetCWErrorLogger = function (message) {
    logger.error(message);
  };

  // Process any deferred metrics.
  if (window.Katal.deferredAssetMetrics) {
    for (const metric in window.Katal.deferredAssetMetrics) {
      window.Katal.internalMetricsAssetLogger(
        metric,
        window.Katal.deferredAssetMetrics[metric]
      );
    }

    window.Katal.deferredAssetMetrics = {};
  }

  // Process any deferred errors.
  if (window.Katal.deferredErrorLogs) {
    window.Katal.deferredErrorLogs.forEach(errorMessage => {
      window.Katal.internalAssetCWErrorLogger(errorMessage);
    });

    window.Katal.deferredErrorLogs = [];
  }

  logScriptErrors(publisher);
  logAssetStatus(publisher, sushiDriver);
  logger.addErrorListener(filterError);

  katalReady(() => {
    let katalReadyTime = null;
    if (performance.now) {
      katalReadyTime = Math.round(performance.now());
    }

    const { ComponentsStart, ComponentsEnd } = window.Katal.Performance;
    if (ComponentsStart && ComponentsEnd) {
      publisher.publishTimerMonitor(
        'katalComponentsExecuteStart',
        ComponentsStart
      );
      publisher.publishTimerMonitor('katalComponentsExecuteEnd', ComponentsEnd);
      publisher.publishTimerMonitor(
        'katalComponentsExecute',
        ComponentsEnd - ComponentsStart
      );
    }

    domReady(() => {
      if (katalReadyTime) {
        publisher.publishTimerMonitor('katalReady', katalReadyTime);
      }

      logBrowserMetrics(publisher);
      logBrowserPerformanceMetrics(publisher);
    });
  });
}

/**
 * Captures and logs errors created by our scripts.
 * Includes syntax and run-time errors.
 */
function logScriptErrors(publisher) {
  window.addEventListener('error', evt => {
    const { filename, error } = evt;
    if (
      filename.match(/katal.*components/) ||
      error?.stack?.match(/katal.*components/)
    ) {
      // No need to log more than a count our errors are already published to RTLA automatically (at least in SellerCentral).
      publisher.publishCounterMonitor('KatalComponentsJSError:count', 1);
    } else if (filename.match('custom-elements_1_2_2.min.js')) {
      publisher.publishCounterMonitor('KatalCustomElementsJSError:count', 1);
    }
  });
}

/**
 * Just before the page unloads (user navigates away or closes tab) send a metric to measure if our assets have loaded successfully or not.
 */
function logAssetStatus(publisher, sushiDriver) {
  if (!window.performance || !window.performance.getEntriesByType) {
    return;
  }

  sushiDriver.beforeUnload(() => {
    const resources = performance.getEntriesByType('resource');
    if (!resources) {
      return;
    }

    const found = [];
    resources.forEach(entry => {
      const type = getResourceType(entry);
      if (type) {
        found.push(type);
      }
    });

    ['KatalCSS', 'KatalComponentJS', 'KatalPolyfill'].forEach(resourceType => {
      publisher.publishCounterMonitor(
        `Unload.${resourceType}.Loaded`,
        found.indexOf(resourceType) !== -1 ? 1 : 0
      );
    });
  });
}

/**
 * Logs information about the browser such as polyfill status
 */
function logBrowserMetrics(publisher) {
  if (!window.WebComponents || !window.WebComponents.requiredPolyfills) {
    return;
  }

  const logPolyfillMetric = (alias, metricName) => {
    publisher.publishCounterMonitor(
      metricName,
      window.WebComponents.requiredPolyfills.indexOf(alias) !== -1 ? 1 : 0
    );
  };

  // These aliases come from the
  // @webcomponents/webcomponentsjs/webcomponents-loader.js file
  logPolyfillMetric('sd', 'shadyDom');
  logPolyfillMetric('ce', 'customElements');
  logPolyfillMetric('pf', 'platform');
}

/**
 * Time To Interactive measures how long it takes a page to become interactive
 * https://developers.google.com/web/tools/lighthouse/audits/time-to-interactive
 *
 * Google long term hopes to standardize this metric with the PerformanceObserver API,
 * however this is not widely supported.  Their pollyfill requires us to have full control of the dom to inline a script
 * https://developers.google.com/web/fundamentals/performance/user-centric-performance-metrics#tracking_tti
 *
 * Whitepaper on First Interactive and Consistently Interactive
 * https://docs.google.com/document/d/1GGiI9-7KeY3TPqS3YT271upUVimo-XiL5mwWorDUD4c/preview#
 *
 * For this phase we will just use what is provided by the browser performance api
 * https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/domInteractive
 * Its used by companies like netflix
 * https://medium.com/netflix-techblog/making-netflix-com-faster-f95d15f2e972
 *
 */
function logBrowserPerformanceMetrics(publisher) {
  if (!window.performance || !window.performance.getEntriesByType) {
    publisher.publishCounterMonitor('PerformanceApiSupported', 0);
    return;
  }
  publisher.publishCounterMonitor('PerformanceApiSupported', 1);

  logNavigationMetrics(publisher);
  logResourceMetrics(publisher);
  logPaintMetrics(publisher);
}

function logNavigationMetrics(publisher) {
  const navEntries = performance.getEntriesByType('navigation');
  if (navEntries != null && navEntries.length > 0) {
    const navMetrics = navEntries[0];
    if (
      navMetrics.domInteractive !== null &&
      navMetrics.domInteractive !== undefined
    ) {
      publisher.publishTimerMonitor(
        'tti',
        Math.round(navMetrics.domInteractive)
      );
    }
  }
}

function logResourceMetrics(publisher) {
  const resources = performance.getEntriesByType('resource');
  if (!resources) {
    return;
  }
  resources.forEach(r => {
    const resourceType = getResourceType(r);

    //if its one of our known resource types log basic metrics about it, otherwise skip it
    if (!resourceType) {
      return;
    }
    //PerformanceResourceTiming Metrics
    //https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming

    //Checking if these are greater than 0 because there must be a special header written called Timing-Allow-Origin
    //S3 does not provide us a way to set this header so we will only be able to get these metrics on our Integration Test Page
    if (r.transferSize > 0) {
      publisher.publishCounterMonitor(
        `${resourceType}.transferSize`,
        r.transferSize
      );
    }

    if (r.encodedBodySize > 0) {
      publisher.publishCounterMonitor(
        `${resourceType}.encodedBodySize`,
        r.encodedBodySize
      );
    }

    if (r.decodedBodySize > 0) {
      publisher.publishCounterMonitor(
        `${resourceType}.decodedBodySize`,
        r.decodedBodySize
      );
    }
    publisher.publishTimerMonitor(
      `${resourceType}.duration`,
      Math.round(r.duration)
    );
  });
}

function logPaintMetrics(publisher) {
  if (!performance.getEntriesByName) {
    return;
  }

  const firstPaint = performance.getEntriesByName('first-paint');
  if (firstPaint && firstPaint.length > 0) {
    publisher.publishTimerMonitor(
      'FirstPaint',
      Math.round(firstPaint[0].startTime)
    );
  }

  const firstContentfulPaint = performance.getEntriesByName(
    'first-contentful-paint'
  );
  if (firstContentfulPaint && firstContentfulPaint.length > 0) {
    publisher.publishTimerMonitor(
      'FirstContentfulPaint',
      Math.round(firstContentfulPaint[0].startTime)
    );
  }
}

//We mainly only care about metrics for the flo components
//in the future this may change
const ASSET_MATCHERS = {
  KatalCSS: /.+katal\.flo\.components.+css$/,
  KatalAssetLoader: /.+katal\.assetloader.+js$/,
  KatalComponentJS: /.+katal\.flo.components.+js$/,
  // TODO: rename this to be clear that it is the webcomponents polyfills
  KatalPolyfill: /webcomponents-.+\.js$/,
};
function getResourceType(resource) {
  // Guard clause to protect against large data: URI's
  // Customer complaint: in our case was causing page lockup of up to a minute in
  // a metrics.js regex name check loop (39kb data uri font)
  if (resource.name?.startsWith('data:')) {
    return null;
  }
  for (const key in ASSET_MATCHERS) {
    if (ASSET_MATCHERS[key].test(resource.name)) {
      return key;
    }
  }
  return null;
}
